import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';

import localization from '../../../../shared/strings';
import Shipping from '../../Shipping';
import { SHIPPING_PRESETS } from '../../helpers/productDetailsPresets';
import { aggregatedShippingFieldsPropType } from '../helpers/aggregatedFieldPropTypes';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';
import {
  getAggregatedCommonBoolean,
  getAggregatedCommonNumber,
  getAggregatedCommonString,
} from '../helpers/aggregatedFieldHelpers';

const l10n = localization.PRODUCT_INFO;

const AggregatedShippingSection = ({
  shippingFields,
  readOnly,
  readOnlyTooltip,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
  useVariantPreset,
}) => {
  const requiresShippingBindings = useBindAggregatedField(
    'requiresShipping',
    shippingFields?.requiresShipping,
    l10n.physicalProduct,
  );
  const weightBindings = useBindAggregatedField(
    'weight',
    shippingFields?.weightValue,
    l10n.autoMappedFieldLabels.weight,
  );
  const countryBindings = useBindAggregatedField(
    'countryCodeOfOrigin',
    shippingFields?.countryCodeOfOrigin,
    l10n.inventory.countryCodeOfOrigin,
  );
  const harmonizedBindings = useBindAggregatedField(
    'harmonizedSystemCode',
    shippingFields?.harmonizedSystemCode,
    l10n.inventory.harmonizedSystemCode,
  );

  const onChangeRequiresShipping = useCallback((value) => {
    requiresShippingBindings.onChange(value);
  }, [requiresShippingBindings]);

  const onChangeWeight = useCallback((weight) => {
    if (!weight) {
      weightBindings.onChange(null);
      return;
    }

    weightBindings.onChange({
      value: weight.value === '' || weight.value == null ? 0 : Number(weight.value),
      unit: weight.unit || 'grams',
    });
  }, [weightBindings]);

  const onChangeCountryCodeOfOrigin = useCallback((value) => {
    countryBindings.onChange(value);
  }, [countryBindings]);

  const onChangeHarmonizedSystemCode = useCallback((value) => {
    harmonizedBindings.onChange(value);
  }, [harmonizedBindings]);

  if (!shippingFields) return null;

  const weightUnit = getAggregatedCommonString(shippingFields.weightUnit, 'grams') || 'grams';
  const weightValue = getAggregatedCommonNumber(shippingFields.weightValue, 0);

  return (
    <Shipping
      {...(useVariantPreset ? SHIPPING_PRESETS.variant : SHIPPING_PRESETS.simple)}
      aggregatedFields={shippingFields}
      requiresShipping={getAggregatedCommonBoolean(shippingFields.requiresShipping, false)}
      currentValue={{ value: weightValue, unit: weightUnit }}
      countryCodeOfOrigin={getAggregatedCommonString(shippingFields.countryCodeOfOrigin, '')}
      harmonizedSystemCode={getAggregatedCommonString(shippingFields.harmonizedSystemCode, '')}
      readOnly={readOnly}
      readOnlyTooltip={readOnlyTooltip}
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle || l10n.shippingCompliance}
      onChangeRequiresShipping={onChangeRequiresShipping}
      onChangeWeight={onChangeWeight}
      onChangeCountryCodeOfOrigin={onChangeCountryCodeOfOrigin}
      onChangeHarmonizedSystemCode={onChangeHarmonizedSystemCode}
    />
  );
};

AggregatedShippingSection.propTypes = {
  shippingFields: aggregatedShippingFieldsPropType,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node,
  useVariantPreset: PropTypes.bool,
};

AggregatedShippingSection.defaultProps = {
  shippingFields: null,
  readOnly: false,
  readOnlyTooltip: null,
  blockTitle: null,
  useVariantPreset: false,
};

export default memo(AggregatedShippingSection);
